import classNames from 'classnames';

import { BUTTON_SIZE, BUTTON_VARIANT, ButtonSize, ButtonVariant } from '../../styles/generated/data/types';
import { Responsive, responsiveClasses, withBaseDefault } from '../helpers/responsive';

// The variant and size vocabularies are generated from library/presets/button.presets.json — the
// same source that declares them to `mixins/rc-button.scss`, which validates every mixin call
// against them and drives the size emission loops. Aliased to the plural names the component,
// stories and tests already use.
//
// `BUTTON_SIZES` runs mini → large, the Sass emission order.
export const BUTTON_SIZES = BUTTON_SIZE;
export const BUTTON_VARIANTS = BUTTON_VARIANT;
export type { ButtonSize, ButtonVariant };

export interface ButtonStyling {
  /**
   * Button style variants to help create visual hierarchy.
   *
   * Note: the `link` variant is boxless, link-styled text (inherited color, always underlined)
   * that inherits the surrounding text's size for type and icon — the `size` prop is ignored — for
   * an action that should be an RCButton/RCButtonLink but present as a link. Being boxless, its tap
   * target (WCAG 2.5.8) relies on surrounding spacing rather than a min-height. Not available in
   * icon-only mode (a link is text by nature).
   *
   * See [button variant descriptions / guidelines](https://cadence.reverb.tools/design-system/components/button/guidelines/#variant)
   * for more information.
   */
  variant?: ButtonVariant;

  /**
   * The size of the button. Use `small` and `mini` sparingly. Responsive: pass a
   * `ButtonSize`, or a `{ desktop, tablet, mobile }` object to size per breakpoint
   * (e.g. `{ desktop: 'medium', mobile: 'small' }`) — prefer this over a
   * `useMediaQuery` size swap, which flashes on SSR hydration.
   */
  size?: Responsive<ButtonSize>;

  /** Sets `width: 100%` so the button fills its available space. Responsive: pass a
   *  boolean, or a `{ desktop, tablet, mobile }` object (e.g. `{ mobile: true }` to go
   *  full-width only on mobile). */
  fullWidth?: Responsive<boolean>;

  /** Optimizes padding for a leading (before-text) icon. */
  hasLeadingIcon?: boolean;

  /** Optimizes padding for a trailing (after-text) icon. */
  hasTrailingIcon?: boolean;

  /** Sizes the button for an icon with no text. */
  iconOnly?: boolean;

  /** Collapses to icon-only at the given breakpoint. */
  iconOnlyAt?: 'mobile' | 'tablet';

  /** Applies the loading treatment. The loading indicator itself is markup, so this only
   *  styles an element that renders one (RCButton's `loading` prop). */
  loading?: boolean;
}

export const DEFAULT_SIZE: ButtonSize = 'medium';
export const DEFAULT_VARIANT: ButtonVariant = 'muted';

/**
 * Function to generate className string to style any HTML element like an RCButton.
 * This is mainly for use on CoreLinks.
 *
 * @example
 * ```
 * <CoreLink
 *   to={something}
 *   className={buttonClassName({ variant: 'filled' })}
 * >
 *   I look like a button
 * </CoreLink>
 * ```
 */
export function buttonClassName({
  variant = DEFAULT_VARIANT,
  size = DEFAULT_SIZE,
  fullWidth = false,
  hasLeadingIcon = false,
  hasTrailingIcon = false,
  iconOnly = false,
  iconOnlyAt = undefined,
  loading = false,
}: ButtonStyling): string {
  return classNames(
    'rc-button',
    {
      // color and state modifiers
      'rc-button--filled': variant === 'filled',
      'rc-button--outlined': variant === 'outlined',
      'rc-button--muted': variant === 'muted',
      'rc-button--transparent': variant === 'transparent',
      'rc-button--light': variant === 'light',
      'rc-button--elevated-light': variant === 'elevated-light',
      'rc-button--dark': variant === 'dark',
      'rc-button--themed-outlined': variant === 'themed-outlined',
      'rc-button--loud': variant === 'loud',
      'rc-button--danger': variant === 'danger',
      'rc-button--danger-muted': variant === 'danger-muted',
      'rc-button--link': variant === 'link',

      'rc-button--icon-before': hasLeadingIcon,
      'rc-button--icon-after': hasTrailingIcon,
      'rc-button--icon-only': iconOnly,
      'rc-button--icon-only-at-mobile': iconOnlyAt === 'mobile',
      'rc-button--icon-only-at-tablet': iconOnlyAt === 'tablet',
      'rc-button--loading': loading,
    },
    // Size classes: one per breakpoint. `withBaseDefault` guarantees a base-tier size
    // even when the caller passes only `{ tablet }` / `{ mobile }`, since a button with
    // no size class has undefined size custom properties.
    responsiveClasses(withBaseDefault(size, DEFAULT_SIZE), sizeClass),
    responsiveClasses(fullWidth, fullWidthClass),
  );
}

// One `size` value at a breakpoint → its size modifier class, following RCButton's
// `--{modifier}-at-{bp}` convention (cf. `rc-button--full-width-at-mobile`): the base
// tier is the bare `rc-button--{size}`, tablet/mobile get the `-at-{bp}` suffix.
function sizeClass(size: ButtonSize, prefix: string): string {
  // `responsiveClasses` passes '' | 'tablet-' | 'mobile-'; map to the `-at-{bp}` suffix.
  const breakpoint = prefix.replace('-', '');
  const suffix = breakpoint ? `-at-${breakpoint}` : '';
  return `rc-button--${size}${suffix}`;
}

// One `fullWidth` value at a breakpoint → its width modifier class, following RCButton's
// own `--{modifier}-at-{bp}` convention (cf. `rc-button--icon-only-at-mobile`), not the
// utility-layer `{bp}-` prefix. `true` fills the width; `false` at a smaller breakpoint
// needs an explicit `auto-width` override because the base `rc-button--full-width` has no
// upper bound (absence wouldn't undo it). At the base tier `false` is the default (auto)
// and emits nothing.
function fullWidthClass(fullWidth: boolean, prefix: string): string | false {
  // `responsiveClasses` passes '' | 'tablet-' | 'mobile-'; map to the `-at-{bp}` suffix.
  const breakpoint = prefix.replace('-', '');
  const suffix = breakpoint ? `-at-${breakpoint}` : '';
  if (fullWidth) return `rc-button--full-width${suffix}`;
  return breakpoint ? `rc-button--auto-width${suffix}` : false;
}
